import Flex from '@Resources/styles/flex.scss';
import { useAuth0 } from '@auth0/auth0-react';
import { createColumnHelper } from '@tanstack/react-table';
import cx from 'classnames';
import _debounce from 'lodash/debounce';
import _isNil from 'lodash/isNil';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import TextInput from '@Components/DomElements/TextInput';
import ModalFooter from '@Components/ModalComponents/ModalFooter';
import { IDepartment } from '@Data/departments/Interfaces';
import { departmentsState } from '@Data/departments/Reducer';
import ProcessesApi from '@Data/processes/Api';
import { tryCreateProcessNotificationForTask } from '@Data/processes/Helpers';
import { IProcess, IRunningProcess } from '@Data/processes/Interfaces';
import { linkDepartmentProcessState } from '@Data/processes/Reducer';
import { TaskType } from '@Data/processes/TaskType';
import ProjectsApi from '@Data/projects/Api';
import { getParsedAuth0Username } from '@Services/authentication/Helpers';
import DepartmentsTable from '@Views/Project/components/DepartmentsTable/DepartmentsTable';
import Styles from './ConnectDept.scss';

interface IDept {
    id: number;
    name: string;
}

interface IConnectDeptStepProps {
    teamId: string;
    projectId: number;
    onCancel: () => void;
    onComplete: () => void;
    setCancelDisabled: (disabled: boolean) => void;
    setFooter: (footer: JSX.Element) => void;
}

export const ConnectDeptStep = (props: IConnectDeptStepProps) => {
    const { projectId, onCancel, onComplete, setCancelDisabled, setFooter } =
        props;

    const departments = useSelector(departmentsState);
    const linkDepartmentProcess = useSelector(linkDepartmentProcessState);
    const { user } = useAuth0();
    const [filter, setFilter] = useState<string>('');
    const [selectedDept, setSelectedDept] = useState<IDepartment | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const filteredDepartments =
        filter === ''
            ? departments
            : departments?.filter((dept) => {
                  const escapedSearchTerm = filter.replace(
                      /[.*+?^${}()|[\]\\]/g,
                      '\\$&'
                  );
                  const regex = new RegExp(escapedSearchTerm, 'i');
                  return regex.test(dept.name);
              });

    useEffect(() => {
        ProcessesApi.getProcesses();
    }, []);

    // if our selected dept isn't in the list of filtered depts, clear it
    useEffect(() => {
        if (
            _isNil(filteredDepartments) ||
            (!_isNil(selectedDept) &&
                !filteredDepartments.includes(selectedDept))
        ) {
            setSelectedDept(null);
        }
    }, [filteredDepartments, selectedDept]);

    const handleFilterChange = _debounce((e) => {
        setFilter(e.target.value);
    }, 500);

    const columnHelper = createColumnHelper<IDept>();

    const columns = [
        columnHelper.accessor('name', {
            cell: (info) => info.getValue(),
        }),
    ];

    const resetProcessState = () => {
        ProcessesApi.setProcess(null);
        setIsSubmitting(false);
    };

    const handleComplete = async () => {
        const processSchema = {
            departmentId: selectedDept?.id,
            departmentName: selectedDept?.name,
        };

        setIsSubmitting(true);
        setCancelDisabled(true);
        await ProcessesApi.runProcess(
            linkDepartmentProcess,
            processSchema,
            projectId,
            getParsedAuth0Username(user.sub),
            (runningProcess: IRunningProcess) => {
                tryCreateProcessNotificationForTask(
                    runningProcess.process_name,
                    runningProcess.task_id,
                    TaskType.SYNC
                );
            }
        );
        await ProjectsApi.getAndSelectProjectById(
            props.teamId,
            props.projectId
        );
        resetProcessState();
        onComplete();
    };

    const innerHtml = _isNil(filteredDepartments) ? (
        <div
            className={cx(Flex.flex, Flex.justifyCenter)}
            style={{ marginTop: '8px' }}
        >
            <span>You don't have access to any IKE Office Departments</span>
        </div>
    ) : (
        <div className={Styles.inputContainer}>
            <TextInput
                id="department-filter"
                onChange={handleFilterChange}
                placeholder="Search department by name"
                style={{ marginBottom: '1rem' }}
                autoFocus
            />
            {filter.length !== 0 && filteredDepartments.length === 0 ? (
                <div
                    className={cx(Flex.flex, Flex.justifyCenter)}
                    style={{ marginTop: '8px' }}
                >
                    <span>No departments found matching that name</span>
                </div>
            ) : (
                <div className={Styles.tableContainer}>
                    <DepartmentsTable
                        columns={columns}
                        data={filteredDepartments}
                        onRowClick={(dept) => setSelectedDept(dept)}
                        selectedRow={selectedDept}
                        isSubmitting={isSubmitting}
                    />
                </div>
            )}
        </div>
    );

    useEffect(() => {
        setFooter(
            <ModalFooter
                id="connect-dept-footer"
                onCancel={onCancel}
                onSubmit={handleComplete}
                submitText="Connect Department"
                canSubmit={
                    selectedDept !== null && !_isNil(linkDepartmentProcess)
                }
                isSubmitting={isSubmitting}
            />
        );
    }, [isSubmitting, selectedDept, linkDepartmentProcess]);

    return (
        <div className={cx(Flex.flex, Flex.column, Styles.container)}>
            {innerHtml}
        </div>
    );
};
